import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoCollabService } from '../../services/fiko-collab.service';
import { CollabUser } from './fiko-collab-types';

/**
 * FikoCollabUserListComponent - Displays connected users and follow controls
 *
 * Shows:
 * - List of users in the current room
 * - User colors and names
 * - Follow/Unfollow buttons for each user
 * - Connection status
 */
@Component({
    selector: 'fiko-collab-user-list',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="collab-user-list" [class.connected]="collabService.isConnected()">
            <!-- Header -->
            <div class="collab-header">
                <span class="status-dot" [class.online]="collabService.isConnected()"></span>
                <span class="title">Kullanicilar</span>
                <span class="count">({{ userCount() }})</span>
            </div>

            <!-- User List -->
            <div class="user-items">
                @for (user of (collabService.connectedUsers() ?? []); track user.userId) {
                    <div
                        class="user-item"
                        [style.border-left-color]="user.userColor"
                        [class.inactive]="!user.isActive"
                        [class.following]="isFollowing(user.userId)"
                        (click)="toggleFollow(user.userId)"
                        [title]="isFollowing(user.userId) ? 'Takibi birak' : 'Kamerayi takip et'"
                        style="cursor: pointer;"
                    >
                        <!-- Avatar -->
                        <div
                            class="user-avatar"
                            [style.background-color]="user.userColor"
                        >
                            {{ getInitial(user.userName) }}
                        </div>

                        <!-- Name + Status -->
                        <div class="user-info">
                            <span class="user-name">{{ user.userName }}</span>
                            @if (isFollowing(user.userId)) {
                                <span class="following-badge">Takip Ediliyor</span>
                            }
                        </div>

                        <!-- Follow Icon -->
                        <div class="follow-icon" [class.active]="isFollowing(user.userId)">
                            @if (isFollowing(user.userId)) {
                                <span>&#128065;</span>
                            } @else {
                                <span>&#8599;</span>
                            }
                        </div>
                    </div>

                    <!-- Camera Sync Info (under user item) -->
                    @if (getUserCameraData(user.userId)) {
                        <div class="camera-sync-info" [style.border-left-color]="user.userColor">
                            <div class="sync-row">
                                <span class="sync-icon" [class.active]="isCameraDataFresh(user.userId)">&#128247;</span>
                                <span class="sync-label">Kamera:</span>
                                <span class="sync-value">{{ formatPosition(getUserCameraData(user.userId)?.position) }}</span>
                            </div>
                            <div class="sync-row">
                                <span class="sync-icon target">&#127919;</span>
                                <span class="sync-label">Hedef:</span>
                                <span class="sync-value">{{ formatPosition(getUserCameraData(user.userId)?.target) }}</span>
                            </div>
                            <div class="sync-row">
                                <span class="sync-icon" [class.syncing]="isCameraDataFresh(user.userId)">&#128259;</span>
                                <span class="sync-label">Senkron:</span>
                                <span class="sync-value" [class.fresh]="isCameraDataFresh(user.userId)">
                                    {{ getTimeSinceUpdate(user.userId) }}
                                </span>
                            </div>
                        </div>
                    }
                }

                @if ((collabService.connectedUsers()?.length ?? 0) === 0) {
                    <div class="no-users">
                        Baska kullanici yok
                    </div>
                }
            </div>

            <!-- Following Indicator -->
            @if (collabService.isFollowing()) {
                <div class="following-indicator">
                    <span>Takip ediliyor: {{ getFollowedUserName() }}</span>
                    <button class="stop-follow-btn" (click)="stopFollowing()">
                        Dur
                    </button>
                </div>
            }
        </div>
    `,
    styles: [`
        .collab-user-list {
            font-size: 10px;
            background: linear-gradient(135deg, rgba(18,22,30,0.95), rgba(25,30,42,0.95));
            border: 1px solid rgba(0,255,255,0.3);
            border-radius: 6px;
            padding: 6px;
            min-width: 160px;
            color: #fff;
            text-shadow: 0 0 8px rgba(0,255,255,0.6);
        }

        .collab-user-list.connected {
            border-color: rgba(0,255,255,0.5);
        }

        .collab-header {
            display: flex;
            align-items: center;
            gap: 6px;
            padding-bottom: 6px;
            border-bottom: 1px solid rgba(0,255,255,0.2);
            margin-bottom: 6px;
        }

        .status-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #666;
        }

        .status-dot.online {
            background: #0f0;
            box-shadow: 0 0 6px #0f0;
        }

        .title {
            font-weight: bold;
            flex: 1;
        }

        .count {
            opacity: 0.7;
        }

        .user-items {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .user-item {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px;
            border-left: 3px solid transparent;
            border-radius: 3px;
            background: rgba(255,255,255,0.05);
            transition: background 0.2s;
        }

        .user-item:hover {
            background: rgba(0,255,255,0.15);
            border-left-color: currentColor !important;
        }

        .user-item.inactive {
            opacity: 0.5;
        }

        .user-item.following {
            background: rgba(0,255,0,0.15);
            border: 1px solid rgba(0,255,0,0.5);
            box-shadow: 0 0 8px rgba(0,255,0,0.3);
        }

        .user-avatar {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: bold;
            color: #fff;
            text-shadow: none;
            flex-shrink: 0;
        }

        .user-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;
            overflow: hidden;
        }

        .user-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 11px;
        }

        .following-badge {
            font-size: 8px;
            color: #0f0;
            text-shadow: 0 0 4px #0f0;
            animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }

        .follow-icon {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            opacity: 0.6;
            transition: all 0.2s;
        }

        .follow-icon.active {
            opacity: 1;
            color: #0f0;
            text-shadow: 0 0 6px #0f0;
            animation: pulse 1.5s infinite;
        }

        .user-item:hover .follow-icon {
            opacity: 1;
        }

        .no-users {
            text-align: center;
            padding: 8px;
            opacity: 0.6;
            font-style: italic;
        }

        .following-indicator {
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px solid rgba(0,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;
        }

        .following-indicator span {
            font-size: 9px;
            color: #0f0;
        }

        .stop-follow-btn {
            padding: 2px 6px;
            border: none;
            border-radius: 3px;
            background: rgba(255,100,100,0.3);
            color: #f66;
            cursor: pointer;
            font-size: 9px;
        }

        .stop-follow-btn:hover {
            background: rgba(255,100,100,0.5);
        }

        /* Camera Sync Info Styles */
        .camera-sync-info {
            margin-left: 8px;
            margin-bottom: 6px;
            padding: 4px 6px;
            border-left: 2px solid rgba(0,255,255,0.3);
            background: rgba(0,0,0,0.2);
            border-radius: 0 4px 4px 0;
            font-size: 8px;
        }

        .sync-row {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 1px 0;
        }

        .sync-icon {
            width: 12px;
            font-size: 9px;
            opacity: 0.5;
        }

        .sync-icon.active {
            opacity: 1;
            color: #0f0;
            animation: pulse 1s infinite;
        }

        .sync-icon.target {
            color: #ff0;
        }

        .sync-icon.syncing {
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }

        .sync-label {
            color: rgba(255,255,255,0.5);
            width: 40px;
        }

        .sync-value {
            color: #0ff;
            font-family: monospace;
            font-size: 8px;
        }

        .sync-value.fresh {
            color: #0f0;
            text-shadow: 0 0 4px #0f0;
        }
    `]
})
export class FikoCollabUserListComponent {
    collabService = inject(FikoCollabService);

    userCount = computed(() => this.collabService.connectedUsers()?.length ?? 0);

    getInitial(name: string): string {
        return name ? name.charAt(0).toUpperCase() : '?';
    }

    isFollowing(userId: string): boolean {
        return this.collabService.followingUserId() === userId;
    }

    toggleFollow(userId: string): void {
        if (this.isFollowing(userId)) {
            this.collabService.stopFollowing();
        } else {
            this.collabService.startFollowing(userId);
        }
    }

    stopFollowing(): void {
        this.collabService.stopFollowing();
    }

    getFollowedUserName(): string {
        const followedId = this.collabService.followingUserId();
        if (!followedId) return '';

        const user = this.collabService.connectedUsers()?.find(u => u.userId === followedId);
        return user?.userName || '';
    }

    // Camera sync info methods
    getUserCameraData(userId: string): { position: [number, number, number]; target: [number, number, number]; lastUpdate: number } | undefined {
        return this.collabService.remoteUserCameras()?.get(userId);
    }

    formatPosition(pos: [number, number, number] | undefined): string {
        if (!pos) return '---';
        return `${pos[0].toFixed(0)}, ${pos[1].toFixed(0)}, ${pos[2].toFixed(0)}`;
    }

    isCameraDataFresh(userId: string): boolean {
        const data = this.getUserCameraData(userId);
        if (!data) return false;
        return (Date.now() - data.lastUpdate) < 2000; // Fresh if updated within 2 seconds
    }

    getTimeSinceUpdate(userId: string): string {
        const data = this.getUserCameraData(userId);
        if (!data) return '---';

        const elapsed = Date.now() - data.lastUpdate;
        if (elapsed < 1000) return 'Canli';
        if (elapsed < 5000) return `${Math.floor(elapsed / 1000)}sn once`;
        if (elapsed < 60000) return `${Math.floor(elapsed / 1000)}sn`;
        return `${Math.floor(elapsed / 60000)}dk`;
    }
}
